import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import MosqueFilters from "./MosqueFilters";
import { getMosqueTranslation, useTranslation } from "../../../utils/i18nStub";
import { useLanguage } from "../../../contexts/LanguageContext";
import "./MosqueFilters.css";
import { MosqueFilterState } from "../../../types/mosque";

import MapModal from "../../maps/MapModal";
import Breadcrumbs, { Crumb } from "../../../components/navigation/Breadcrumbs";
import { Link, useNavigate } from "react-router-dom";
import { isAuthenticated } from "../../../services/authService";

/**
 * Uses:
 *   Mosque: GET /api/mosque/donation/categories, GET /api/donations/mosques/opportunities
 *   Hygiene: GET /api/donations/hygiene/categories/json, GET /api/donations/hygiene/opportunities/json
 */

const mosqueSvc =
  require("../../../services/mosqueDonationService") as typeof import("../../../services/mosqueDonationService");
const hygieneSvc =
  require("../../../services/hygieneDonationService") as typeof import("../../../services/hygieneDonationService");

const HYGIENE_CAT_A_ID = 900001; // عند اختياره نستخدم واجهة النظافة

type Option = { id: number; name: string };

type DonationFilterState = {
  catAId: number | null;
  catBId: number | null;
  catCId: number | null;
  catDId: number | null;
};

type OpportunityRow = {
  request_id: number;
  request_name: string;

  mosque_id: number;
  number: string;
  mosque_name: string;
  latitude: number;
  longitude: number;
  capacity: number | null;
  women_prayer_room_capacity: number | null;
  has_women_prayer_room: string | null;

  classification_id: number | null;
  classification_name: string | null;
  region_name: string | null;
  city_name: string | null;
  moia_center_name: string | null;
  full_address: string | null;
  district_name: string | null;

  cat_a_id: number | null;
  cat_a_name: string | null;
  cat_b_id: number | null;
  cat_b_name: string | null;
  cat_c_id: number | null;
  cat_c_name: string | null;
  cat_d_id: number | null;
  cat_d_name: string | null;

  cat_e_ids?: number[];
  cat_e_names?: string | null;
  cat_c_names?: string | null;

  total_count?: number;
  page_count?: number;
};

const toOption = (row: any): Option => ({
  id: row?.id ?? row?.Id ?? 0,
  name: row?.name ?? row?.Name ?? "",
});

const emptyMosqueFilters: MosqueFilterState = {
  regionId: null,
  cityId: null,
  centerId: null,
  classificationId: null,
  mosqueTypeId: null,
  hasWomenSection: null,

  // ✅ NEW: shared mosque text filters
  mosqueName: null,
  mosqueNumber: null,

};

const emptyDonationFilters: DonationFilterState = {
  catAId: null,
  catBId: null,
  catCId: null,
  catDId: null,
};

const DEFAULT_CAT_A_NAME = "أعمال الصيانة التصحيحية والوقائية";

export default function MosqueDonationOpportunitiesPage() {
  const { t } = useTranslation("mosque");
  const { language } = useLanguage();
  const navigate = useNavigate(); // ✅ HOOK MUST BE INSIDE COMPONENT
  const catALabel =
    getMosqueTranslation(language, "donation.filters.catA") ??
    t("donation.filters.catA", "فئة نوع المساهمة");

  // ----------------------------------------
  // Breadcrumbs (match MosqueSearchPage)
  // ----------------------------------------
  const crumbs: Crumb[] = useMemo(
    () => [
      { label: "الرئيسية", to: "/" },
      { label: "فرص عقود الصيانة والنظافة" },
    ],
    []
  );

  // ----------------------------------------
  // State
  // ----------------------------------------
  const [mosqueFilters, setMosqueFilters] =
    useState<MosqueFilterState>(emptyMosqueFilters);
  const [donationFilters, setDonationFilters] =
    useState<DonationFilterState>(emptyDonationFilters);

  // Applied filters: used for API (queryString). Updated only when user clicks Apply.
  const [appliedMosqueFilters, setAppliedMosqueFilters] =
    useState<MosqueFilterState>(emptyMosqueFilters);
  const [appliedDonationFilters, setAppliedDonationFilters] =
    useState<DonationFilterState>(emptyDonationFilters);

  const [pageNo, setPageNo] = useState(1);
  const pageSize = 12;

  const [catA, setCatA] = useState<Option[]>([]);
  const [catB, setCatB] = useState<Option[]>([]);
  const [catC, setCatC] = useState<Option[]>([]);
  const [catD, setCatD] = useState<Option[]>([]);
  const [hygieneCatB, setHygieneCatB] = useState<Option[]>([]);
  const [hygieneCatC, setHygieneCatC] = useState<Option[]>([]);
  const [loadingLookups, setLoadingLookups] = useState(false);
  const [itemsSource, setItemsSource] = useState<"mosque" | "hygiene">("mosque");

  // للعرض الفوري: خيارات B و C تتغير مباشرة عند تغيير A (بدون تطبيق)
  const isHygieneMode = donationFilters.catAId === HYGIENE_CAT_A_ID;
  const displayCatB = isHygieneMode ? hygieneCatB : catB;
  const displayCatC = isHygieneMode ? hygieneCatC : catC;

  // Results
  const [items, setItems] = useState<OpportunityRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageCount, setPageCount] = useState(0);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // ID لفئة الصيانة الافتراضية — يُستخدم للـ API عندما catAId فارغ (القائمة تُظهر "يرجى الإختيار")
  const [defaultMaintenanceCatAId, setDefaultMaintenanceCatAId] = useState<number | null>(null);

  // Reset key so MosqueFilters remounts when we clear mosque filters
  const [filtersResetKey, setFiltersResetKey] = useState(0);

  // Map modal
  const [mapItem, setMapItem] = useState<OpportunityRow | null>(null);
  const openMap = (it: OpportunityRow) => setMapItem(it);
  const closeMap = () => setMapItem(null);

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoadingLookups(true);
      try {
        const [mosqueA, mosqueB, mosqueC, mosqueD, hygieneData] = await Promise.all([
          mosqueSvc.listCatA(),
          mosqueSvc.listCatB(),
          mosqueSvc.listCatC(),
          mosqueSvc.listCatD(),
          hygieneSvc.getHygieneCategories().catch(() => null),
        ]);

        if (!alive) return;

        const mosqueAOpts = (mosqueA ?? []).map(toOption);
        const hygieneAOpt = hygieneData?.catA?.[0];
        const catAWithHygiene = hygieneAOpt
          ? [...mosqueAOpts, { id: HYGIENE_CAT_A_ID, name: hygieneAOpt.name }]
          : mosqueAOpts;

        setCatA(catAWithHygiene);
        setCatB((mosqueB ?? []).map(toOption));
        setCatC((mosqueC ?? []).map(toOption));
        setCatD((mosqueD ?? []).map(toOption));
        setHygieneCatB((hygieneData?.catB ?? []).map(toOption));
        setHygieneCatC((hygieneData?.catC ?? []).map(toOption));

        // ID افتراضي لنتائج الصيانة — القائمة تُظهر "يرجى الإختيار" لكن النتائج = أعمال الصيانة
        const defaultOpt = mosqueAOpts.find((o) =>
          o.name.includes("الصيانة التصحيحية") || o.name === DEFAULT_CAT_A_NAME
        );
        if (defaultOpt) {
          setDefaultMaintenanceCatAId(defaultOpt.id);
        }
      } catch {
        // ignore
      } finally {
        if (alive) setLoadingLookups(false);
      }
    })();

    return () => { alive = false; };
  }, []);

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    const f = appliedDonationFilters;
    const effectiveCatAId = f.catAId ?? defaultMaintenanceCatAId;
    const isHyg = effectiveCatAId === HYGIENE_CAT_A_ID;

    if (appliedMosqueFilters.regionId) p.set("regionId", String(appliedMosqueFilters.regionId));
    if (appliedMosqueFilters.cityId) p.set("cityId", String(appliedMosqueFilters.cityId));
    if (appliedMosqueFilters.centerId) p.set("centerId", String(appliedMosqueFilters.centerId));
    if (appliedMosqueFilters.classificationId)
      p.set("classificationId", String(appliedMosqueFilters.classificationId));

    const hasWomen = appliedMosqueFilters.hasWomenSection;
    if (hasWomen !== null && hasWomen !== undefined) {
      p.set(isHyg ? "hasWomenPrayerRoom" : "hasWomenSection", hasWomen ? "true" : "false");
    }

    // ✅ NEW: optional mosque text filters
    if (appliedMosqueFilters.mosqueName?.trim()) {
      p.set("mosqueName", appliedMosqueFilters.mosqueName.trim());
    }

    if (appliedMosqueFilters.mosqueNumber?.trim()) {
      p.set("mosqueNumber", appliedMosqueFilters.mosqueNumber.trim());
    }

    if (effectiveCatAId != null) {
      if (isHyg) {
        p.set("catAId", "1");
        if (f.catBId) p.set("catBId", String(f.catBId));
        if (f.catCId) p.set("catCId", String(f.catCId));
      } else {
        p.set("catAId", String(effectiveCatAId));
        if (f.catBId) p.set("catBId", String(f.catBId));
        if (f.catCId) p.set("catCId", String(f.catCId));
        if (f.catDId) p.set("catDId", String(f.catDId));
      }
    }

    p.set("pageNo", String(pageNo));
    p.set("pageSize", String(pageSize));
    return p.toString();
  }, [appliedMosqueFilters, appliedDonationFilters, defaultMaintenanceCatAId, pageNo]);

  // ----------------------------------------
  // Fetch
  // ----------------------------------------
  const effectiveCatAId = appliedDonationFilters.catAId ?? defaultMaintenanceCatAId;

  const fetchOpportunities = React.useCallback(async () => {
    const effId = effectiveCatAId;
    const qs = queryString;
    if (effId == null) return;

    try {
      setLoading(true);
      setErr(null);

      const isHyg = effId === HYGIENE_CAT_A_ID;
      let list: OpportunityRow[] = [];
      let total = 0;
      let pages = 0;

      if (isHyg) {
        setItemsSource("hygiene");
        const res = await hygieneSvc.listHygieneOpportunities(qs);
        list = Array.isArray(res?.data) ? res.data : Array.isArray(res?.items) ? res.items : Array.isArray(res) ? res : [];
        const meta = res?.meta ?? {};
        total = meta.total_count ?? meta.totalCount ?? (list[0] as any)?.total_count ?? 0;
        pages = meta.page_count ?? meta.pageCount ?? (list[0] as any)?.page_count ?? 0;
      } else {
        setItemsSource("mosque");
        const data = await mosqueSvc.listOpportunities(qs);
        list = Array.isArray(data) ? data : (data?.items ?? []);
        total = list[0] ? ((list[0] as any).total_count ?? 0) : (data?.totalCount ?? 0);
        pages = list[0] ? ((list[0] as any).page_count ?? 0) : (data?.pageCount ?? 0);
      }

      setItems(list);
      setTotalCount(total);
      setPageCount(pages);
    } catch (e: any) {
      setErr(e?.message ?? "Failed to load opportunities.");
      setItems([]);
      setTotalCount(0);
      setPageCount(0);
    } finally {
      setLoading(false);
    }
  }, [effectiveCatAId, queryString]);

  useEffect(() => {
    if (effectiveCatAId == null) {
      setItems([]);
      setTotalCount(0);
      setPageCount(0);
      setLoading(false);
      return;
    }
    fetchOpportunities();
  }, [effectiveCatAId, fetchOpportunities]);

  // Apply mosque filters (embedded: only sync state; refetch happens on bottom Apply)
  const onApplyMosqueFilters = (f: MosqueFilterState) => {
    setMosqueFilters(f);
  };

  // Single Apply / Reset for the merged filters container
  const onApplyAllFilters = () => {
    setAppliedMosqueFilters(mosqueFilters);
    setAppliedDonationFilters(donationFilters);
    setPageNo(1);
  };
  const onResetAllFilters = () => {
    setDonationFilters(emptyDonationFilters);
    setMosqueFilters(emptyMosqueFilters);
    setAppliedMosqueFilters(emptyMosqueFilters);
    setAppliedDonationFilters(emptyDonationFilters);
    setFiltersResetKey((k) => k + 1);
    setPageNo(1);
  };

  const loggedIn = isAuthenticated();

  // ----------------------------------------
  // Render (match MosqueSearchPage layout)
  // ----------------------------------------
  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-16">
      <div className="space-y-6">
        {/* ✅ Pass crumbs correctly (your component doesn't accept "crumbs") */}
        <Breadcrumbs items={crumbs} />

        {loggedIn && (
          <div className="flex justify-end">
            <Link
              to="/mosque/my-requests"
              className="inline-flex items-center justify-center rounded-xl bg-sa-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sa-700"
            >
              طلباتي
            </Link>
          </div>
        )}

        {/* Single merged filters container (same style as MosqueFilters) */}
        <div className="modern-filters-container">
          <div className="modern-filters-form space-y-6">
            {/* Section: Mosque filters (region, city, center, classification, etc.) */}
            <div>
              <MosqueFilters key={`mosque-filters-${filtersResetKey}`} embedded initial={mosqueFilters} onApply={onApplyMosqueFilters} />
            </div>

            {/* Section: Donation category filters */}
            <div className="border-t border-gray-200/60 pt-4">
              <div className="flex items-end gap-2 flex-wrap">
                {/* Cat A */}
                <div className="filter-group flex-1 min-w-[160px]">
                  <label className="filter-label font-arabic text-xs mb-1">
                    {catALabel}
                  </label>
                  <div className="filter-input-wrapper">
                    <select
                      className="modern-filter-select font-arabic"
                      disabled={loadingLookups}
                      value={donationFilters.catAId ?? ""}
                      onChange={(e) => {
                        const v = e.target.value ? Number(e.target.value) : null;
                        setDonationFilters((p) => {
                          const next = {
                            ...p,
                            catAId: v,
                            catBId: null,
                            catCId: null,
                            catDId: null,
                          };
                          setAppliedDonationFilters(next);
                          setPageNo(1);
                          return next;
                        });
                      }}
                    >
                      <option value="">{t("donation.filters.selectPlaceholder", "يرجى الإختيار")}</option>
                      {catA.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                    <div className="filter-arrow">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Cat B */}
                <div className="filter-group flex-1 min-w-[160px]">
                  <label className="filter-label font-arabic text-xs mb-1">
                    {t("donation.filters.catB", "فئة أعمال نوع المساهمة")}
                  </label>
                  <div className="filter-input-wrapper">
                    <select
                      className="modern-filter-select font-arabic"
                      disabled={loadingLookups}
                      value={donationFilters.catBId ?? ""}
                      onChange={(e) => {
                        const v = e.target.value ? Number(e.target.value) : null;
                        setDonationFilters((p) => {
                          const next = { ...p, catBId: v };
                          setAppliedDonationFilters(next);
                          setPageNo(1);
                          return next;
                        });
                      }}
                    >
                      <option value="">{t("donation.filters.all", "الكل")}</option>
                      {displayCatB.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                    <div className="filter-arrow">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Cat C: قائمة منسدلة للصيانة والنظافة */}
                <div className="filter-group flex-1 min-w-[160px]">
                  <label className="filter-label font-arabic text-xs mb-1">
                    {t("donation.filters.catC", "فئة أعمال الصيانة")}
                  </label>
                  <div className="filter-input-wrapper">
                    <select
                      className="modern-filter-select font-arabic"
                      disabled={loadingLookups}
                      value={donationFilters.catCId ?? ""}
                      onChange={(e) =>
                        setDonationFilters((p) => ({
                          ...p,
                          catCId: e.target.value ? Number(e.target.value) : null,
                        }))
                      }
                    >
                      <option value="">{t("donation.filters.all", "الكل")}</option>
                      {displayCatC.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                    <div className="filter-arrow">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Cat D - معطلة عند وضع النظافة */}
                <div className="filter-group flex-1 min-w-[160px]">
                  <label className="filter-label font-arabic text-xs mb-1">
                    {t("donation.filters.catD", "فئة تفاصيل أعمال الصيانة")}
                  </label>
                  <div className="filter-input-wrapper">
                    <select
                      className="modern-filter-select font-arabic"
                      disabled={loadingLookups || isHygieneMode}
                      value={donationFilters.catDId ?? ""}
                      onChange={(e) => {
                        const v = e.target.value ? Number(e.target.value) : null;
                        setDonationFilters((p) => {
                          const next = { ...p, catDId: v };
                          setAppliedDonationFilters(next);
                          setPageNo(1);
                          return next;
                        });
                      }}
                    >
                      <option value="">{t("donation.filters.all", "الكل")}</option>
                      {catD.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                    <div className="filter-arrow">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Single Apply + Reset at bottom (same button style as MosqueFilters) */}
            <div className="flex items-center justify-end gap-2 border-t border-gray-200/60 pt-4">
              <button
                type="button"
                onClick={onResetAllFilters}
                className="px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center gap-1.5 text-xs whitespace-nowrap h-[42px]"
                disabled={loading}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3,6 5,6 21,6" />
                  <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2" />
                </svg>
                {t("donation.filters.reset", "مسح")}
              </button>
              <button
                type="button"
                onClick={onApplyAllFilters}
                className="px-4 py-2 bg-sa-600 text-white rounded-lg hover:bg-sa-700 transition-colors duration-200 flex items-center gap-1.5 text-xs whitespace-nowrap h-[42px]"
                disabled={loading}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
                {t("donation.filters.apply", "تطبيق")}
              </button>
            </div>
          </div>
        </div>

        {/* Result meta */}
        <div className="flex items-center justify-between mb-2">
          <div className="text-neutral-500 text-sm">
            {t("donation.results.total", "إجمالي النتائج")}:{" "}
            <strong className="text-neutral-800">{totalCount}</strong>
          </div>

          {loading && (
            <div className="text-neutral-500 text-sm flex items-center gap-2">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" role="status" aria-label="loading">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" />
                <path className="opacity-75" d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" fill="none" />
              </svg>
              {t("search.loading", "جاري التحميل...")}
            </div>
          )}
        </div>

        {err && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {err}
          </div>
        )}

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {items.map((x) => {
            const latNum = x?.latitude == null ? NaN : Number(x.latitude);
            const lngNum = x?.longitude == null ? NaN : Number(x.longitude);
            const canShowMap = Number.isFinite(latNum) && Number.isFinite(lngNum);

            // // ✅ NEW: Determine DDR vs HDR for this row based on current data source
            // // - itemsSource === "hygiene"  => HDR flow
            // // - itemsSource === "mosque"   => DDR flow
             const kind: "ddr" | "hdr" = itemsSource === "hygiene" ? "hdr" : "ddr";

            
            const contributionSerial = x.request_name || `DDR${x.request_id}`;

            return (
              <div key={x.request_id} className="h-full">
                <article className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 flex flex-col h-full relative">
                  {/* Header row: icon right, serial left */}
                  <div className="mb-4 flex items-start justify-between">
                    <img
                      src={`${process.env.PUBLIC_URL}/dga_v2/assets/img/moiaIcons/mosqueIcon1.svg`}
                      alt="أيقونة المسجد"
                      className="w-10 h-10"
                      width={40}
                      height={40}
                      loading="lazy"
                      draggable={false}
                      style={{
                        filter:
                          "brightness(0) saturate(100%) invert(25%) sepia(95%) saturate(1200%) hue-rotate(130deg) brightness(85%) contrast(95%)",
                      }}
                    />
                    <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full shadow-sm bg-emerald-50 text-emerald-700 border border-emerald-200 font-arabic">
                      {contributionSerial}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-gray-900 mb-3 text-right leading-tight line-clamp-2 min-h-[56px] font-arabic">
                    {x.mosque_name || t("unknown_mosque", "مسجد غير معروف")}
                  </h3>

                  {/* Address */}
                  <div className="mb-4 text-right">
                    <div className="flex items-start gap-2 text-right">
                      <svg className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="text-gray-600 text-sm leading-relaxed flex-1 font-arabic">
                        {x.full_address || "—"}
                      </span>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="space-y-2.5 flex-1 mb-4">
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-500 text-sm font-medium font-arabic">
                        {t("box.number", "الرقم التسلسلي للمسجد")}
                      </span>
                      <span className="text-gray-900 font-semibold text-sm">{x.number || "—"}</span>
                    </div>

                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-500 text-sm font-medium font-arabic">
                        {t("box.city_region", "المدينة / المنطقة")}
                      </span>
                      <span className="text-gray-900 font-semibold text-sm font-arabic">
                        {x.city_name
                          ? `${x.city_name}${x.region_name ? "، " + x.region_name : ""}`
                          : (x.region_name || "—")}
                      </span>
                    </div>

                    <div className="pt-1 text-gray-700 font-arabic">
                      <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                        <div className="space-y-2 text-sm">
                          <div className="flex items-start gap-2">
                            <span className="shrink-0 inline-flex items-center rounded-full border border-gray-300 bg-white px-2 py-0.5 text-xs font-semibold text-gray-800">
                              A
                            </span>
                            <span className="leading-relaxed break-words whitespace-normal flex-1">
                              {x.cat_a_name ?? "-"}
                            </span>
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="shrink-0 inline-flex items-center rounded-full border border-gray-300 bg-white px-2 py-0.5 text-xs font-semibold text-gray-800">
                              B
                            </span>
                            <span className="leading-relaxed break-words whitespace-normal flex-1">
                              {x.cat_b_name ?? "-"}
                            </span>
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="shrink-0 inline-flex items-center rounded-full border border-gray-300 bg-white px-2 py-0.5 text-xs font-semibold text-gray-800">
                              C
                            </span>
                            <span className="leading-relaxed break-words whitespace-normal flex-1">
                              {x.cat_c_names ?? x.cat_c_name ?? "-"}
                            </span>
                          </div>
                          {itemsSource === "mosque" && (
                            <>
                              <div className="flex items-start gap-2">
                                <span className="shrink-0 inline-flex items-center rounded-full border border-gray-300 bg-white px-2 py-0.5 text-xs font-semibold text-gray-800">
                                  D
                                </span>
                                <span className="leading-relaxed break-words whitespace-normal flex-1">
                                  {x.cat_d_name ?? "-"}
                                </span>
                              </div>
                              <div className="flex items-start gap-2">
                                <span className="shrink-0 inline-flex items-center rounded-full border border-gray-300 bg-white px-2 py-0.5 text-xs font-semibold text-gray-800">
                                  E
                                </span>
                                <span className="leading-relaxed break-words whitespace-normal flex-1">
                                  {x.cat_e_names || "-"}
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Footer buttons */}
                  <div className="mt-auto pt-4 border-t border-gray-100">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {/* ✅ ENABLED + ROUTED */}

                      {/* // ✅ Determine which flow this row belongs to (Maintenance DDR vs Hygiene HDR).
                      // We keep it super explicit to avoid any ambiguity across pages. */}
                      {/* const kind: "ddr" | "hdr" = itemsSource === "hygiene" ? "hdr" : "ddr"; */}

                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();

                          // ✅ Pass kind in BOTH:
                          // 1) router state (best for internal navigation)
                          // 2) querystring (survives refresh, copy/paste URL, direct open)
                          navigate(`/mosque/donation-details/${x.request_id}?kind=${kind}`, {
                            state: {
                              fromList: true,
                              backTo: "/mosque/donation-opportunities",
                              item: x,

                              // ✅ New: source-of-truth flag for DDR/HDR routing
                              kind,

                              // ✅ Keep existing field for backward compatibility (if any page uses it)
                              source: itemsSource === "hygiene" ? "hygiene" : undefined,
                            },
                          });
                        }}
                        className="w-full rounded-xl bg-sa-600 px-6 py-3 text-base font-semibold text-white hover:bg-sa-700 transition-all duration-300"
                      >
                        عرض التفاصيل
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openMap(x);
                        }}
                        className={`w-full h-11 flex items-center justify-center gap-2 rounded-lg border text-sm font-semibold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 font-arabic ${
                          canShowMap
                            ? "border-gray-300 text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400"
                            : "border-gray-200 text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-200"
                        }`}
                        title={
                          canShowMap
                            ? t("box.buttons.map", "استعراض الخريطة")
                            : t("box.map_unavailable", "الخريطة غير متاحة")
                        }
                      >
                        {t("box.buttons.map", "استعراض الخريطة")}
                      </button>
                    </div>
                  </div>
                </article>
              </div>
            );
          })}
        </div>

        {/* Pager */}
        {pageCount > 1 && (
          <div className="flex items-center justify-center gap-3 pt-4">
            <button
              className="h-10 px-4 rounded-lg border border-neutral-300 text-neutral-700 text-sm hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
              disabled={pageNo <= 1}
              onClick={() => setPageNo((p) => Math.max(1, p - 1))}
            >
              {t("list.prev", "السابق")}
            </button>
            <div className="text-neutral-500 text-sm font-arabic">
              {t("list.pageOf", "صفحة {{page}} من {{total}}", { page: pageNo, total: pageCount })}
            </div>
            <button
              className="h-10 px-4 rounded-lg border border-neutral-300 text-neutral-700 text-sm hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
              disabled={pageNo >= pageCount}
              onClick={() => setPageNo((p) => p + 1)}
            >
              {t("list.next", "التالي")}
            </button>
          </div>
        )}

        {/* No results / Select Cat A prompt */}
        {!loading && items.length === 0 && (
          <div className="text-center py-12">
            <div className="text-neutral-500">
              <img
                src={`${process.env.PUBLIC_URL}/dga_v2/assets/img/moiaIcons/mosque.svg`}
                alt=""
                width={64}
                height={64}
                className="mx-auto mb-3 select-none"
                aria-hidden="true"
                draggable={false}
              />
              <p className="font-arabic text-neutral-700">
                {effectiveCatAId == null
                  ? t("donation.results.selectCatA", "يرجى اختيار فئة نوع المساهمة لعرض النتائج")
                  : t("donation.results.noResults", "لا توجد نتائج")}
              </p>
            </div>
          </div>
        )}

        {/* Map modal */}
        <MapModal
          open={!!mapItem}
          onClose={closeMap}
          lat={mapItem?.latitude ?? null}
          lng={mapItem?.longitude ?? null}
          title={mapItem?.full_address ?? (mapItem?.mosque_name ?? "")}
        />
      </div>
    </div>
  );
}
